export interface GcsCredentials {
  credentials: string
  bucket: string
  expiry: Date
}

export interface RefreshGcsCredentialsParams {
  rolodexUrl: string
  entryId: string
  authorizedAxios: {
    get: (url: string) => Promise<{
      data: { credentials: string; bucket: string; expiry: string }
    }>
  }
  currentCredentials?: GcsCredentials | null
}

/**
 * Refreshes GCS credentials if they are expired or missing.
 * Returns the current credentials if they are still valid, otherwise fetches new ones.
 */
export async function refreshGcsCredentials({
  rolodexUrl,
  entryId,
  authorizedAxios,
  currentCredentials,
}: RefreshGcsCredentialsParams): Promise<GcsCredentials> {
  if (currentCredentials && Date.now() < currentCredentials.expiry.getTime()) {
    return currentCredentials
  }

  const url = `${rolodexUrl}/api/v2/files/shortlivedcreds/${entryId}?action=create`

  try {
    const res = await authorizedAxios.get(url)
    const data = res.data

    if (!data.credentials || !data.bucket || !data.expiry) {
      throw new Error('Invalid credentials response')
    }

    return {
      credentials: data.credentials,
      bucket: data.bucket,
      expiry: new Date(data.expiry + 'Z'),
    }
  } catch (error) {
    console.error('Failed to get GCS credentials:', error)
    throw error
  }
}

export interface UploadFileToGcsParams {
  file: File | Blob
  fullPath: string
  credentials: GcsCredentials
}

/**
 * Uploads a file to Google Cloud Storage.
 * @param file - The file to upload
 * @param fullPath - The full path where the file should be stored (e.g., "entryId/photos/raw/filename.jpg")
 * @param credentials - The GCS credentials to use for authentication
 */
export async function uploadFileToGcs({
  file,
  fullPath,
  credentials,
}: UploadFileToGcsParams): Promise<void> {
  const sanitizedPath = fullPath.replaceAll('/', '%2F')
  const gcsUrl = `https://storage.googleapis.com/upload/storage/v1/b/${credentials.bucket.slice(5)}/o?uploadType=media&name=${sanitizedPath}`

  const response = await fetch(gcsUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${credentials.credentials}`,
      'Content-Type': 'application/octet-stream',
    },
    body: file,
  })

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status} ${response.statusText}`)
  }
}
